import {ChangeDetectionStrategy, Component, input, numberAttribute, output, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {TranslateModule} from "@ngx-translate/core";
import {MatButton} from "@angular/material/button";
import {FormControl} from "@angular/forms";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {tillatteBildeMimetyper} from "felles-ng2/støttefunksjoner/mimetyper";
import {FilvelgerViaInputFelt} from "felles-ng2/tjenester/filopplasting/FilvelgerViaInputFelt";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {FormaterFilstørrelsePipe} from "felles-ng2/pipes/FormaterFilstørrelsePipe";
import {Base64DataUrl} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";

const debug = initDebugger('BildeVelgerKomponent');

/**
 * Skjema for å velge bilde, eksponerer valgt bilde som base64 enkodet data URL.
 * Base64DataUrl har formen "data:[mimetype];base64,[base64enkodede-data]",
 * se JS metoden {@link FileReader#readAsDataURL}
 */
@Component({
    selector: 'bilde-velger',
    templateUrl: 'bilde-velger.html',
    styleUrl: 'bilde-velger.scss',
    host: {'data-testid': 'bilde-velger'},
    imports: [
        TranslateModule,
        MatButton,
        FormaterFilstørrelsePipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BildeVelgerKomponent {
    static readonly standardMaksBildestørrelse = 1000000;

    /** Kalles med bilde-velger (filvelger) vises. */
    vedBildevelgerAktivert = output();
    /** Kalles med bilde-fil som bruker har valgt, null dersom bruker avbryter 'velg fil'-dialogen */
    vedBildeValgt = output<File | null>();

    skjemakontroll = input.required<FormControl<Base64DataUrl | null>>();
    maksStoerrelse = input(BildeVelgerKomponent.standardMaksBildestørrelse, {transform: numberAttribute});
    /** Dersom sann vil 'velg bilde'-knappen være disabled */
    velgBildeKnappDeaktivert = input(false);

    bildeForStor = signal(false);

    private bildefil: File | null = null;

    constructor(private filvelgerViaInputFelt: FilvelgerViaInputFelt) {

    }

    async velgFil() {
        this.vedBildevelgerAktivert.emit();
        const valgt = første(await this.filvelgerViaInputFelt.presenterFilvelger({tillateMimetyper: tillatteBildeMimetyper(), tillatMultiple: false}));
        if (erNil(valgt)) {
            this.skjemakontroll().setValue(null);
            this.vedBildeValgt.emit(null);
            return;
        }
        await this.filValgt(valgt);
    }

    //private hjelpemetoder

    private async filValgt(bilde: File) {
        if (bilde.size > this.maksStoerrelse()) {
            this.bildeForStor.set(true);
            this.vedBildeValgt.emit(null);
            return;
        }
        this.bildefil = bilde;
        this.bildeForStor.set(false);
        this.skjemakontroll().setValue(await this.valgtBildeSomBase64())
        this.vedBildeValgt.emit(bilde);
    }

    private valgtBildeSomBase64(): Promise<Base64DataUrl | null> {
        if (erNil(this.bildefil)) return Promise.resolve(null);
        return new Promise<Base64DataUrl>((resolve, reject) => {
            const leser = new FileReader();
            leser.readAsDataURL(this.bildefil!);
            leser.onload = () => resolve(<Base64DataUrl>leser.result);
            leser.onerror = error => reject(error);
        });
    }
}
